import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Settings as SettingsIcon, Phone, ShieldAlert, Presentation } from "lucide-react";
import { toast } from "sonner";
import { MeetingLocationsSection } from "@/components/settings/MeetingLocationsSection";
import { OfficeContactSection } from "@/components/settings/OfficeContactSection";
import { BusinessRulesSection } from "@/components/settings/BusinessRulesSection";
import { PresentationModeSection } from "@/components/settings/PresentationModeSection";

const SECTIONS = [
  { key: "meeting_locations", label: "Meeting Locations", icon: MapPin },
  { key: "office_contact", label: "Office Contact", icon: Phone },
  { key: "business_rules", label: "Business Rules", icon: ShieldAlert },
  { key: "presentation_mode", label: "Presentation Mode", icon: Presentation },
];

const emptyBusinessRules = {
  cancellation_window_hours: 24,
  cancellation_fee_amount: 25,
  grace_period_days: 7,
  reminder_3day_enabled: true,
  reminder_1day_enabled: true,
  business_start_time: "08:00",
  business_end_time: "18:00",
  business_days: ["mon", "tue", "wed", "thu", "fri", "sat"],
  required_driving_lessons: 20,
  required_classroom_hours: 30,
  required_observation_hours: 6,
};

export default function Settings() {
  const [activeSection, setActiveSection] = useState("meeting_locations");
  const [locations, setLocations] = useState([]);
  const [officeContact, setOfficeContact] = useState({ phone: "", email: "" });
  const [businessRules, setBusinessRules] = useState(emptyBusinessRules);
  const [demoCta, setDemoCta] = useState({ url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [demoCtaSaving, setDemoCtaSaving] = useState(false);
  const [newLocation, setNewLocation] = useState("");

  const load = useCallback(async () => {
    try {
      const [locRes, contactRes, rulesRes, demoCtaRes] = await Promise.all([
        api.get("/settings/meeting-locations"),
        api.get("/settings/office-contact"),
        api.get("/settings/business-rules"),
        api.get("/settings/demo-cta"),
      ]);
      setLocations(locRes.data);
      setOfficeContact(contactRes.data);
      setBusinessRules(rulesRes.data);
      setDemoCta({ url: demoCtaRes.data?.url || "" });
    } catch {
      toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveOfficeContact = async () => {
    setContactSaving(true);
    try {
      const res = await api.put("/settings/office-contact", officeContact);
      setOfficeContact(res.data);
      toast.success("Office contact info saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save office contact");
    } finally {
      setContactSaving(false);
    }
  };

  const addLocation = () => {
    if (!newLocation.trim()) return;
    setLocations((prev) => [...prev, { id: `new-${Date.now()}`, name: newLocation.trim(), active: true }]);
    setNewLocation("");
  };

  const renameLocation = (id, name) => setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, name } : l)));
  const toggleActive = (id) => setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, active: !l.active } : l)));
  const removeUnsaved = (id) => setLocations((prev) => prev.filter((l) => l.id !== id));

  const save = async () => {
    if (locations.some((l) => !l.name.trim())) {
      toast.error("Location names cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const payload = locations.map((l) => ({ id: l.id?.startsWith("new-") ? undefined : l.id, name: l.name.trim(), active: l.active }));
      const res = await api.put("/settings/meeting-locations", { locations: payload });
      setLocations(res.data);
      toast.success("Meeting locations saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleBusinessDay = (day) => {
    setBusinessRules((r) => {
      const days = r.business_days || [];
      return { ...r, business_days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day] };
    });
  };

  const saveBusinessRules = async () => {
    setRulesSaving(true);
    try {
      const res = await api.put("/settings/business-rules", {
        ...businessRules,
        cancellation_window_hours: Number(businessRules.cancellation_window_hours),
        cancellation_fee_amount: Number(businessRules.cancellation_fee_amount),
        grace_period_days: Number(businessRules.grace_period_days),
        required_driving_lessons: Number(businessRules.required_driving_lessons),
        required_classroom_hours: Number(businessRules.required_classroom_hours),
        required_observation_hours: Number(businessRules.required_observation_hours),
      });
      setBusinessRules(res.data);
      toast.success("Business rules saved — DriveDesk Hub will enforce these automatically");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save business rules");
    } finally {
      setRulesSaving(false);
    }
  };

  const saveDemoCta = async () => {
    setDemoCtaSaving(true);
    try {
      const res = await api.put("/settings/demo-cta", { url: demoCta.url || null });
      setDemoCta({ url: res.data?.url || "" });
      toast.success("Presentation Mode settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save Presentation Mode settings");
    } finally {
      setDemoCtaSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><SettingsIcon className="h-6 w-6" /> Business Rules & Settings</h1>
        <p className="text-sm text-muted-foreground">Configure company policies so DriveDesk Hub enforces them automatically.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-6">
        <div className="flex sm:flex-col gap-2 sm:w-56 shrink-0" data-testid="settings-section-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`flex items-center gap-2 rounded-[var(--radius)] px-3 py-2.5 text-sm font-medium text-left transition-colors ${
                activeSection === s.key ? "bg-[hsl(var(--role-accent))] text-white" : "hover:bg-secondary text-foreground"
              }`}
              data-testid={`settings-section-${s.key}`}
            >
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>

        <div className="flex-1 rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-4">
          {activeSection === "meeting_locations" && (
            <MeetingLocationsSection
              locations={locations}
              newLocation={newLocation}
              onRenameLocation={renameLocation}
              onToggleActive={toggleActive}
              onRemoveUnsaved={removeUnsaved}
              onNewLocationChange={setNewLocation}
              onAddLocation={addLocation}
              onSave={save}
              saving={saving}
            />
          )}

          {activeSection === "office_contact" && (
            <OfficeContactSection
              officeContact={officeContact}
              onChange={setOfficeContact}
              onSave={saveOfficeContact}
              saving={contactSaving}
            />
          )}

          {activeSection === "business_rules" && (
            <BusinessRulesSection
              businessRules={businessRules}
              onChange={setBusinessRules}
              onToggleBusinessDay={toggleBusinessDay}
              onSave={saveBusinessRules}
              saving={rulesSaving}
            />
          )}

          {activeSection === "presentation_mode" && (
            <PresentationModeSection
              demoCta={demoCta}
              onChange={setDemoCta}
              onSave={saveDemoCta}
              saving={demoCtaSaving}
            />
          )}
        </div>
      </div>
    </div>
  );
}
